import React, { useState } from 'react';
import { Button, Modal, Field, FieldLabel, FieldDescription, FieldError } from '../ui';
import { Lesson } from '@court-sync/shared';

interface ReportAbsenceModalProps {
  isOpen: boolean;
  lesson: Lesson | null;
  lessonDate: string | null;
  notificationDeadlineHours?: number;
  isLoading?: boolean;
  onCancel: () => void;
  onConfirm: (reason?: string) => Promise<void>;
}

export const ReportAbsenceModal: React.FC<ReportAbsenceModalProps> = ({
  isOpen,
  lesson,
  lessonDate,
  notificationDeadlineHours = 24,
  isLoading = false,
  onCancel,
  onConfirm,
}) => {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      setError('');
      await onConfirm(reason);
      setReason('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to report absence');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setReason('');
    setError('');
    onCancel();
  };

  if (!isOpen || !lesson || !lessonDate) {
    return null;
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Report Absence" maxWidth="sm">
      <div className="space-y-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-900">
          <p className="font-medium mb-2">Lesson Details</p>
          <p>{lesson.name}</p>
          <p>{lessonDate}</p>
        </div>

        <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-900">
          <p className="font-medium mb-1">You will earn 1 make-up credit!</p>
          <p>Use it for any lesson of the same type and skill level.</p>
        </div>

        <Field>
          <FieldLabel>Reason for Absence (Optional)</FieldLabel>
          <FieldDescription>
            Tell us why you're missing this lesson (helps us improve)
          </FieldDescription>
          <textarea
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              if (error) setError('');
            }}
            placeholder="E.g., Feeling sick, unexpected work meeting, family emergency"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            rows={3}
            disabled={submitting || isLoading}
          />
        </Field>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900">
          <p className="font-medium mb-1">Important:</p>
          <p>You must report your absence within {notificationDeadlineHours} hours of the lesson to earn the credit.</p>
        </div>

        {error && <FieldError>{error}</FieldError>}

        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={submitting || isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || isLoading}
            isLoading={submitting || isLoading}
          >
            Report Absence
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ReportAbsenceModal;
